import React, { useEffect, useMemo, useState, useDeferredValue } from "react";
import axios from "axios";
import {
  Building2, Search, Wallet, HandCoins, X, Save, Trash2, Edit3,
  FileText, Share2, Printer, Download, Calendar, ArrowLeft, ArrowRightLeft,
  User, Filter, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { smartMatch } from "../../utils/arabicSearch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ar-IQ", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

// Palette
const C = {
  blue: "#2563EB",
  darkBlue: "#1E3A5F",
  green: "#059669",
  orange: "#F97316",
  red: "#DC2626",
  amber: "#D97706",
};

const SupplierDebtsTab = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [onlyOwed, setOnlyOwed] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/suppliers`);
      setSuppliers(Array.isArray(data) ? data : []);
    } catch { toast.error("فشل تحميل المُجهّزين"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchSuppliers(); }, []);

  const filtered = useMemo(() => {
    let out = suppliers;
    if (onlyOwed) out = out.filter((s) => (s.debt || 0) > 0);
    if (deferredSearch.trim()) out = out.filter((s) => smartMatch(`${s.name || ""} ${s.phone || ""}`, deferredSearch));
    return out;
  }, [suppliers, deferredSearch, onlyOwed]);

  const totalOwed = suppliers.reduce((s, x) => s + Math.max(0, x.debt || 0), 0);
  const totalOwedByUs = suppliers.reduce((s, x) => s + (x.debt < 0 ? Math.abs(x.debt) : 0), 0);

  // ─── Detail view for one supplier ───
  if (selected) {
    return (
      <SupplierDetail
        supplier={selected}
        onBack={() => { setSelected(null); fetchSuppliers(); }}
        onPay={() => setShowPayModal(true)}
        onStatement={() => setShowStatement(true)}
        showPayModal={showPayModal}
        setShowPayModal={setShowPayModal}
        showStatement={showStatement}
        setShowStatement={setShowStatement}
        editingVoucher={editingVoucher}
        setEditingVoucher={setEditingVoucher}
      />
    );
  }

  return (
    <div className="space-y-4" dir="rtl">
      {/* Header — colorful gradient */}
      <div className="rounded-2xl overflow-hidden shadow-lg" style={{ background: `linear-gradient(135deg, ${C.darkBlue}, ${C.blue})` }}>
        <div className="p-4 text-white">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-black text-lg">مديونية الشركات</h2>
              <p className="text-[11px] opacity-80">جميع المُجهّزين — من نطلبهم أو يطلبوننا — مع سندات الحوالات</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white/15 rounded-xl p-2.5 backdrop-blur-sm">
              <p className="text-[10px] opacity-80">إجمالي ديوننا</p>
              <p className="font-black text-lg" dir="ltr">{fmt(totalOwed)} د.ع</p>
            </div>
            <div className="bg-white/15 rounded-xl p-2.5 backdrop-blur-sm">
              <p className="text-[10px] opacity-80">إجمالي رصيدنا عندهم</p>
              <p className="font-black text-lg" dir="ltr">{fmt(totalOwedByUs)} د.ع</p>
            </div>
          </div>
        </div>

        <div className="p-3 bg-white space-y-2">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث عن شركة مُجهّز..."
              data-testid="supplier-debt-search"
              className="w-full pr-9 pl-3 py-2.5 rounded-xl border-2 border-blue-100 focus:outline-none focus:border-blue-400 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOnlyOwed((v) => !v)}
              data-testid="supplier-only-owed"
              className={`flex-1 py-2 rounded-full text-xs font-bold transition-all flex items-center justify-center gap-1 ${onlyOwed ? "text-white shadow-md" : "bg-slate-100 text-slate-600"}`}
              style={onlyOwed ? { background: C.red } : {}}
            >
              <Filter className="w-3.5 h-3.5" />
              {onlyOwed ? "✓ الذين يطلبوننا فقط" : "الكل"}
            </button>
            <a
              href="#supplier-payments-statement"
              onClick={(e) => { e.preventDefault(); setShowStatement("all"); }}
              data-testid="supplier-payments-statement-btn"
              className="flex-1 py-2 rounded-full text-xs font-bold flex items-center justify-center gap-1 text-white shadow-md"
              style={{ background: C.green }}
            >
              <Receipt className="w-3.5 h-3.5" />
              كشف التسديدات
            </a>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white rounded-2xl border border-slate-100">
          {onlyOwed ? "لا يوجد مُجهّز يطلبنا" : "لا نتائج"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((s) => (
            <button
              type="button"
              key={s.id}
              onClick={() => setSelected(s)}
              data-testid={`supplier-card-${s.id}`}
              className="text-right bg-white rounded-2xl border-2 border-slate-100 p-4 hover:border-blue-400 hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${C.blue}20` }}>
                  <Building2 className="w-5 h-5" style={{ color: C.blue }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-sm text-slate-800 truncate">{s.name}</p>
                  {s.phone && <p className="text-[10px] text-slate-500" dir="ltr">{s.phone}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <div className="rounded-lg p-1.5" style={{ background: `${C.blue}0d` }}>
                  <p className="text-[9px]" style={{ color: C.blue }}>إجمالي مشتريات</p>
                  <p className="font-black" dir="ltr" style={{ color: C.blue }}>{fmt(s.total_invoiced)}</p>
                </div>
                <div className="rounded-lg p-1.5" style={{ background: `${(s.debt || 0) >= 0 ? C.red : C.green}0d` }}>
                  <p className="text-[9px]" style={{ color: (s.debt || 0) >= 0 ? C.red : C.green }}>
                    {(s.debt || 0) >= 0 ? "متبقّي عليها" : "رصيد لنا"}
                  </p>
                  <p className="font-black" dir="ltr" style={{ color: (s.debt || 0) >= 0 ? C.red : C.green }}>{fmt(Math.abs(s.debt || 0))}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Global (all suppliers) payments statement modal */}
      {showStatement === "all" && (
        <AllPaymentsStatement onClose={() => setShowStatement(false)} />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Supplier detail view — full statement + payment button + statement icon
// ═══════════════════════════════════════════════════════════════════════
const SupplierDetail = ({ supplier, onBack, showPayModal, setShowPayModal, showStatement, setShowStatement, editingVoucher, setEditingVoucher }) => {
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStmt = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/suppliers/${supplier.id}/statement`);
      setStmt(data);
    } catch { toast.error("فشل تحميل الكشف"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchStmt(); }, [supplier.id]);

  const s = stmt?.supplier || supplier;
  const debt = s.debt || 0;

  return (
    <div className="space-y-3" dir="rtl">
      <button type="button" onClick={onBack} data-testid="back-to-suppliers"
        className="flex items-center gap-1.5 text-sm font-bold text-[#1E3A5F]">
        <ArrowLeft className="w-4 h-4" /> رجوع لقائمة الشركات
      </button>

      {/* Supplier header */}
      <div className="rounded-2xl overflow-hidden shadow-lg" style={{ background: `linear-gradient(135deg, ${C.darkBlue}, ${C.blue})` }}>
        <div className="p-4 text-white">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
              <Building2 className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <p className="font-black text-xl">{s.name}</p>
              {s.phone && <p className="text-xs opacity-80" dir="ltr">{s.phone}</p>}
              {s.address && <p className="text-xs opacity-80">{s.address}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-white/15 rounded-xl p-2">
              <p className="text-[10px] opacity-80">إجمالي مشتريات</p>
              <p className="font-black text-base" dir="ltr">{fmt(s.total_invoiced)}</p>
            </div>
            <div className="rounded-xl p-2" style={{ background: debt >= 0 ? `${C.red}40` : `${C.green}40` }}>
              <p className="text-[10px] opacity-90">{debt >= 0 ? "متبقّي عليها" : "رصيد لنا"}</p>
              <p className="font-black text-base" dir="ltr">{fmt(Math.abs(debt))}</p>
            </div>
          </div>
        </div>
        <div className="p-3 bg-white grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setShowPayModal(true)}
            data-testid="supplier-pay-btn"
            className="py-2.5 rounded-full text-white font-bold text-sm shadow-md flex items-center justify-center gap-1 hover:brightness-110"
            style={{ background: C.orange }}>
            <HandCoins className="w-4 h-4" /> دفع للمُجهّز
          </button>
          <button type="button" onClick={() => setShowStatement("supplier")}
            data-testid="supplier-statement-btn"
            className="py-2.5 rounded-full text-white font-bold text-sm shadow-md flex items-center justify-center gap-1 hover:brightness-110"
            style={{ background: C.green }}>
            <Receipt className="w-4 h-4" /> كشف تسديداتنا
          </button>
        </div>
      </div>

      {/* Payments list */}
      {loading ? (
        <div className="text-center py-10 text-slate-500">جاري التحميل...</div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2" style={{ background: `${C.orange}0d` }}>
              <HandCoins className="w-4 h-4" style={{ color: C.orange }} />
              <p className="font-black text-sm" style={{ color: C.orange }}>سندات الحوالات إلى هذه الشركة</p>
            </div>
            {(stmt.vouchers || []).length === 0 ? (
              <p className="text-center py-6 text-slate-400 text-sm">لا يوجد تسديدات بعد</p>
            ) : (
              stmt.vouchers.filter((v) => v.kind === "payment_out").map((v) => (
                <VoucherRow key={v.id} v={v} onEdit={() => setEditingVoucher(v)} onDelete={async () => {
                  if (!window.confirm("حذف هذا السند؟")) return;
                  try {
                    await axios.delete(`${API}/admin/supplier-vouchers/${v.id}`);
                    toast.success("تم الحذف");
                    fetchStmt();
                  } catch { toast.error("فشل الحذف"); }
                }} />
              ))
            )}
          </div>
        </>
      )}

      {/* Pay modal */}
      {showPayModal && (
        <PayModal
          supplier={s}
          onClose={() => setShowPayModal(false)}
          onSaved={() => { setShowPayModal(false); fetchStmt(); }}
        />
      )}

      {/* Edit modal */}
      {editingVoucher && (
        <PayModal
          supplier={s}
          editing={editingVoucher}
          onClose={() => setEditingVoucher(null)}
          onSaved={() => { setEditingVoucher(null); fetchStmt(); }}
        />
      )}

      {/* Statement filtered to this supplier */}
      {showStatement === "supplier" && (
        <AllPaymentsStatement
          initialSupplierName={s.name}
          onClose={() => setShowStatement(false)}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Voucher row + Pay modal
// ═══════════════════════════════════════════════════════════════════════
const VoucherRow = ({ v, onEdit, onDelete }) => (
  <div className="p-3 border-b last:border-b-0 border-slate-100 flex items-start gap-3 hover:bg-orange-50/40">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${C.orange}18` }}>
      <ArrowRightLeft className="w-4 h-4" style={{ color: C.orange }} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-black text-base" dir="ltr" style={{ color: C.orange }}>{fmt(v.amount)} د.ع</p>
        <p className="text-[10px] text-slate-500 whitespace-nowrap">{fmtDT(v.transfer_date || v.created_at)}</p>
      </div>
      <p className="text-xs text-slate-600 mt-0.5">
        من <b>{v.from_company || "جملتي"}</b> إلى <b>{v.to_company || v.supplier_name}</b>
      </p>
      {v.transferred_by_name && <p className="text-[11px] text-slate-500 mt-0.5">👤 اسم المُحوِّل: {v.transferred_by_name}</p>}
      {v.note && <p className="text-[11px] text-slate-500 mt-0.5">📝 {v.note}</p>}
      <div className="flex items-center gap-1 mt-2">
        <button type="button" onClick={onEdit} data-testid={`voucher-edit-${v.id}`}
          className="text-[10px] px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-bold flex items-center gap-1 hover:bg-blue-100">
          <Edit3 className="w-3 h-3" /> تعديل
        </button>
        <button type="button" onClick={onDelete} data-testid={`voucher-delete-${v.id}`}
          className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-700 font-bold flex items-center gap-1 hover:bg-red-100">
          <Trash2 className="w-3 h-3" /> حذف
        </button>
      </div>
    </div>
  </div>
);

const PayModal = ({ supplier, editing, onClose, onSaved }) => {
  const [form, setForm] = useState({
    amount: editing?.amount || "",
    from_company: editing?.from_company || "جملتي",
    to_company: editing?.to_company || supplier.name,
    transferred_by_name: editing?.transferred_by_name || "",
    transfer_date: editing?.transfer_date || new Date().toISOString().slice(0, 10),
    note: editing?.note || "",
    paid_from_cash_box: editing?.paid_from_cash_box || false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const amt = Number(form.amount);
    if (!(amt > 0)) { toast.error("اكتب مبلغاً صحيحاً"); return; }
    setBusy(true);
    try {
      if (editing) {
        await axios.patch(`${API}/admin/supplier-vouchers/${editing.id}`, form);
        toast.success("تم تعديل السند");
      } else {
        await axios.post(`${API}/admin/suppliers/${supplier.id}/vouchers`, { ...form, kind: "payment_out" });
        toast.success("تم حفظ الدفعة");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحفظ"); }
    finally { setBusy(false); }
  };

  // ── Export helpers ──
  const buildText = () => {
    const lines = [
      "🏦 سند حوالة",
      "─────────────",
      `المبلغ: ${fmt(form.amount)} د.ع`,
      `من: ${form.from_company}`,
      `إلى: ${form.to_company}`,
      form.transferred_by_name ? `اسم المُحوِّل: ${form.transferred_by_name}` : "",
      `التاريخ: ${form.transfer_date}`,
      form.note ? `الملاحظات: ${form.note}` : "",
    ].filter(Boolean);
    return lines.join("\n");
  };
  const openWhatsapp = () => {
    const text = encodeURIComponent(buildText());
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const doPrint = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!doctype html><html dir=rtl><head><meta charset=utf-8><title>سند حوالة</title>
      <style>body{font-family:Arial,Tahoma;padding:24px;color:#1e293b}h1{color:#1E3A5F;text-align:center}
      .row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #e5e7eb}
      .amt{background:#fff7ed;color:#c2410c;padding:16px;font-size:24px;font-weight:900;text-align:center;border-radius:12px;margin:16px 0}
      </style></head><body>
      <h1>🏦 سند حوالة إلى مُجهّز</h1>
      <div class=amt>${fmt(form.amount)} د.ع</div>
      <div class=row><b>من:</b><span>${form.from_company}</span></div>
      <div class=row><b>إلى:</b><span>${form.to_company}</span></div>
      ${form.transferred_by_name ? `<div class=row><b>اسم المُحوِّل:</b><span>${form.transferred_by_name}</span></div>` : ""}
      <div class=row><b>التاريخ:</b><span dir=ltr>${form.transfer_date}</span></div>
      ${form.note ? `<div class=row><b>ملاحظات:</b><span>${form.note}</span></div>` : ""}
      <script>window.print();</script></body></html>`);
    w.document.close();
  };
  const doPdf = () => {
    // Same rendered document; on mobile the user picks "Save as PDF" in
    // the browser's print dialog. Keeping this dead-simple avoids the
    // heavy jspdf dep.
    doPrint();
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/70 flex items-center justify-center p-3" dir="rtl" data-testid="supplier-pay-modal">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="p-4 text-white sticky top-0" style={{ background: `linear-gradient(135deg, ${C.orange}, ${C.amber})` }}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              <div>
                <h3 className="font-black text-base">{editing ? "تعديل سند الحوالة" : "دفع للمُجهّز"}</h3>
                <p className="text-[11px] opacity-90">{supplier.name}</p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <F label="المبلغ (د.ع)" ltr>
            <input type="number" inputMode="decimal" value={form.amount}
              onChange={(e) => set("amount", e.target.value)}
              data-testid="pay-amount"
              className="w-full px-3 py-3 rounded-xl border-2 border-orange-200 text-2xl font-black text-center focus:outline-none focus:border-orange-400"
              placeholder="0" dir="ltr" />
          </F>
          <div className="grid grid-cols-2 gap-2">
            <F label="من (الشركة المرسلة)">
              <input type="text" value={form.from_company} onChange={(e) => set("from_company", e.target.value)}
                data-testid="pay-from-company" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
            </F>
            <F label="إلى (الشركة المستلمة)">
              <input type="text" value={form.to_company} onChange={(e) => set("to_company", e.target.value)}
                data-testid="pay-to-company" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
            </F>
          </div>
          <F label="اسم المُحوِّل (الشخص اللي حوّل)">
            <input type="text" value={form.transferred_by_name} onChange={(e) => set("transferred_by_name", e.target.value)}
              data-testid="pay-transferred-by" placeholder="مثلاً: أحمد / محمد الشمري"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
          </F>
          <F label="تاريخ الحوالة">
            <input type="date" value={form.transfer_date} onChange={(e) => set("transfer_date", e.target.value)}
              data-testid="pay-transfer-date" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
          </F>
          <F label="ملاحظات">
            <textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)}
              data-testid="pay-note" placeholder="اختياري"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none" />
          </F>
          {!editing && (
            <label className="flex items-center gap-2 text-xs text-slate-700 bg-slate-50 rounded-xl p-2">
              <input type="checkbox" checked={form.paid_from_cash_box}
                onChange={(e) => set("paid_from_cash_box", e.target.checked)}
                data-testid="pay-from-cashbox" />
              <span>خصم من صندوق النقد أيضاً</span>
            </label>
          )}

          {/* Export row */}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <button type="button" onClick={openWhatsapp} data-testid="pay-wa" className="py-2 rounded-full bg-green-100 text-green-800 text-xs font-bold flex items-center justify-center gap-1">
              <Share2 className="w-3.5 h-3.5" /> واتساب
            </button>
            <button type="button" onClick={doPdf} data-testid="pay-pdf" className="py-2 rounded-full bg-red-100 text-red-800 text-xs font-bold flex items-center justify-center gap-1">
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
            <button type="button" onClick={doPrint} data-testid="pay-print" className="py-2 rounded-full bg-blue-100 text-blue-800 text-xs font-bold flex items-center justify-center gap-1">
              <Printer className="w-3.5 h-3.5" /> طباعة
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <button type="button" onClick={onClose} className="py-2.5 rounded-full bg-slate-100 text-slate-600 font-bold text-sm">إلغاء</button>
            <button type="button" onClick={submit} disabled={busy} data-testid="pay-save"
              className="py-2.5 rounded-full text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-1"
              style={{ background: C.orange }}>
              <Save className="w-4 h-4" /> {editing ? "حفظ التعديل" : "حفظ الدفعة"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const F = ({ label, ltr, children }) => (
  <div>
    <label className={`text-[11px] font-bold text-slate-700 block mb-1 ${ltr ? "text-center" : ""}`}>{label}</label>
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════
// All Payments Statement — global "كشف تسديدات"
// ═══════════════════════════════════════════════════════════════════════
const AllPaymentsStatement = ({ initialSupplierName, onClose }) => {
  const [q, setQ] = useState(initialSupplierName || "");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const deferredQ = useDeferredValue(q);
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/supplier-payments`, {
        params: { q: deferredQ, date_from: dateFrom, date_to: dateTo },
      });
      setRows(data.payments || []);
      setTotal(data.total || 0);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [deferredQ, dateFrom, dateTo]);

  return (
    <div className="fixed inset-0 z-[135] bg-black/70 flex items-start justify-center p-3 pt-6 overflow-y-auto" dir="rtl">
      <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden">
        <div className="p-4 text-white sticky top-0" style={{ background: `linear-gradient(135deg, ${C.green}, #10B981)` }}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              <h3 className="font-black text-base">كشف التسديدات للمُجهّزين</h3>
            </div>
            <button type="button" onClick={onClose} data-testid="statement-close"
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-white/15 rounded-xl p-2 text-center">
            <p className="text-[10px] opacity-80">إجمالي التسديدات ({rows.length} سند)</p>
            <p className="font-black text-2xl" dir="ltr">{fmt(total)} د.ع</p>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث باسم الشركة، المُحوِّل، أو الملاحظات..."
              data-testid="statement-search"
              className="w-full pr-9 pl-3 py-2 rounded-xl border-2 border-green-100 focus:outline-none focus:border-green-400 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500">من</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">إلى</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-6 text-slate-500 text-sm">جاري التحميل...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">لا يوجد تسديدات</div>
          ) : (
            <div className="border-2 border-slate-100 rounded-2xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-2 text-right">التاريخ</th>
                    <th className="p-2 text-right">الشركة</th>
                    <th className="p-2 text-center">المبلغ</th>
                    <th className="p-2 text-center"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((v) => (
                    <tr key={v.id} className="border-t border-slate-100 hover:bg-green-50/50">
                      <td className="p-2 whitespace-nowrap">{fmtDT(v.transfer_date || v.created_at)}</td>
                      <td className="p-2 font-bold text-slate-800">{v.supplier_name}</td>
                      <td className="p-2 text-center font-black" dir="ltr" style={{ color: C.orange }}>{fmt(v.amount)}</td>
                      <td className="p-2 text-center">
                        <button type="button" onClick={() => setDetail(v)}
                          data-testid={`statement-row-${v.id}`}
                          className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold hover:bg-blue-200">
                          البطاقة
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail card popup */}
      {detail && (
        <div className="fixed inset-0 z-[140] bg-black/80 flex items-center justify-center p-3" onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden" data-testid="statement-detail">
            <div className="p-4 text-white" style={{ background: `linear-gradient(135deg, ${C.orange}, ${C.amber})` }}>
              <div className="flex items-center justify-between">
                <p className="font-black text-sm">🏦 بطاقة الحوالة</p>
                <button type="button" onClick={() => setDetail(null)} className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-2">
              <div className="rounded-2xl p-3 text-center" style={{ background: `${C.orange}15` }}>
                <p className="text-[10px] font-bold" style={{ color: C.orange }}>المبلغ</p>
                <p className="font-black text-3xl" dir="ltr" style={{ color: C.orange }}>{fmt(detail.amount)} د.ع</p>
              </div>
              <Detail label="من" value={detail.from_company || "جملتي"} />
              <Detail label="إلى" value={detail.to_company || detail.supplier_name} />
              {detail.transferred_by_name && <Detail label="اسم المُحوِّل" value={detail.transferred_by_name} />}
              <Detail label="التاريخ" value={fmtDT(detail.transfer_date || detail.created_at)} />
              {detail.note && <Detail label="ملاحظات" value={detail.note} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-b-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-sm font-bold text-slate-800">{value}</span>
  </div>
);

export default SupplierDebtsTab;
